<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <header class="card-header">
            <div class="d-flex align-items-center">
              <strong class="align-self-center">PMAU - Ferramentas de IA</strong>
            </div>
          </header>
          <div class="card-body">
            <!-- Loading -->
            <div v-if="loading" class="loading-container">
              <RotateSquare
                size="60px"
                class="loading-position animated fadeIn"
              ></RotateSquare>
            </div>

            <!-- Etapas do PMAU -->
            <div v-else-if="stages.length > 0">
              <div class="row">
                <div
                  v-for="stage in stages"
                  :key="stage.code"
                  class="col-sm-12 col-md-6 col-lg-4 mb-3"
                >
                  <div
                    class="card h-100 mb-0"
                    :class="{
                      'border-success': isImplemented(stage),
                      'border-light': !isImplemented(stage)
                    }"
                  >
                    <div
                      class="card-header py-2"
                      :class="{
                        'bg-success text-white': isImplemented(stage),
                        'bg-light text-muted': !isImplemented(stage)
                      }"
                    >
                      <strong>{{ stage.name }}</strong>
                    </div>
                    <div class="card-body d-flex flex-column">
                      <p
                        class="flex-grow-1"
                        :class="{ 'text-muted': !isImplemented(stage) }"
                      >
                        {{ stage.description }}
                      </p>
                      <div>
                        <button
                          v-if="isImplemented(stage)"
                          class="btn btn-primary btn-sm"
                          :disabled="batchGenerating"
                          @click="OpenStage(stage)"
                        >
                          <i class="fa fa-pencil-alt mr-1"></i> Acessar
                        </button>
                        <span v-else class="badge badge-secondary">
                          Em breve
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Painel com Sub-etapas (Accordion) -->
        <div v-if="selectedStage && hasSubStages" class="card" ref="editorPanel">
          <header class="card-header">
            <div class="d-flex">
              <strong class="align-self-center">{{ selectedStage.name }}</strong>
              <div class="ml-auto">
                <button
                  class="btn btn-sm btn-outline-success mr-2"
                  :disabled="batchGenerating || anySubGenerating"
                  @click="GenerateAllSubStages"
                >
                  <i class="fa fa-magic mr-1"></i>
                  {{ batchGenerating
                    ? `Gerando ${batchProgress.current}/${batchProgress.total}...`
                    : "Gerar todo o capítulo" }}
                </button>
                <button
                  class="btn btn-sm btn-outline-dark mr-2"
                  :disabled="batchGenerating || anySubGenerating || exporting"
                  @click="ExportChapter"
                >
                  <i class="fa fa-file-word mr-1"></i>
                  {{ exporting ? "Exportando..." : "Exportar .docx" }}
                </button>
                <button class="btn btn-sm btn-secondary" @click="CloseStage">
                  Voltar
                </button>
              </div>
            </div>
          </header>
          <div class="card-body p-0">
            <!-- Banner de progresso do batch -->
            <div v-if="batchGenerating" class="batch-progress-banner px-3 py-3 border-bottom">
              <div class="d-flex align-items-center mb-2">
                <span class="font-weight-bold text-primary">
                  <i class="fa fa-magic mr-2"></i>
                  Gerando capítulo: {{ batchProgress.current }} de {{ batchProgress.total }} sub-etapas
                </span>
                <button class="btn btn-sm btn-outline-danger ml-auto" @click="CancelBatch">
                  <i class="fa fa-times mr-1"></i> Cancelar
                </button>
              </div>
              <b-progress
                :value="batchProgress.current"
                :max="batchProgress.total"
                animated
                variant="primary"
                height="10px"
                class="mb-2"
              ></b-progress>
              <small class="text-muted">
                <i class="fa fa-spinner fa-spin mr-1"></i>
                {{ batchCurrentName }}
              </small>
            </div>

            <div
              v-for="sub in selectedStage.sub_stages"
              :key="sub.code"
              class="border-bottom"
            >
              <!-- Accordion Header -->
              <div
                class="d-flex align-items-center p-3 accordion-header"
                @click="toggleAccordion(sub.code)"
              >
                <i
                  :class="openPanels[sub.code] ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"
                  class="mr-2"
                ></i>
                <strong>{{ sub.name }}</strong>
                <span v-if="subLoading[sub.code]" class="badge badge-light text-muted ml-2">
                  <i class="fa fa-spinner fa-spin mr-1"></i>Carregando
                </span>
                <span v-else-if="hasContent(subContents[sub.code])" class="badge badge-success ml-2">
                  ● Preenchido
                </span>
                <span v-else class="badge badge-light text-muted ml-2">
                  ○ Vazio
                </span>
                <div class="ml-auto d-flex align-items-center" @click.stop>
                  <small :class="getSubStatusClass(sub.code)" class="mr-2">
                    <i :class="getSubStatusIcon(sub.code)" class="mr-1"></i>
                    {{ getSubStatusText(sub.code) }}
                  </small>
                  <button
                    class="btn btn-sm btn-success mr-1"
                    :disabled="subSaving[sub.code] || subStatuses[sub.code] === 'saved'"
                    @click="FlushSubSave(sub.code)"
                  >
                    <i class="fa fa-save"></i>
                  </button>
                  <button
                    v-if="isSubImplemented(sub.code)"
                    class="btn btn-sm btn-outline-primary"
                    :disabled="subGenerating[sub.code] || batchGenerating"
                    @click="GenerateSubStage(sub)"
                  >
                    <i class="fa fa-magic mr-1"></i>
                    {{ subGenerating[sub.code]
                      ? (usesWebSearch(sub.code) ? "Pesquisando..." : "Gerando...")
                      : "Gerar com IA" }}
                  </button>
                  <span v-else class="badge badge-secondary">Em breve</span>
                </div>
              </div>

              <!-- Accordion Body -->
              <b-collapse :visible="openPanels[sub.code]">
                <div class="p-3 pt-0">
                  <!-- Checkboxes de objetivos (1.3) -->
                  <div v-if="sub.has_selectable_options" class="mb-3">
                    <p class="font-weight-bold mb-2">
                      Selecione os objetivos específicos:
                    </p>
                    <div
                      v-for="opt in selectedStage.objective_options"
                      :key="opt.id"
                      class="form-check mb-2"
                    >
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :id="opt.id"
                        :value="opt.id"
                        v-model="selectedObjectives"
                      />
                      <label class="form-check-label" :for="opt.id">
                        {{ opt.text }}
                      </label>
                    </div>
                  </div>

                  <!-- Loading conteúdo -->
                  <div v-if="subLoading[sub.code]" class="text-center py-4">
                    <RotateSquare
                      size="40px"
                      class="loading-position animated fadeIn"
                    ></RotateSquare>
                  </div>

                  <!-- Loading geração IA -->
                  <div
                    v-else-if="subGenerating[sub.code]"
                    class="text-center py-4"
                  >
                    <RotateSquare
                      size="40px"
                      class="loading-position animated fadeIn"
                    ></RotateSquare>
                    <p class="text-muted mt-2">
                      {{ usesWebSearch(sub.code)
                        ? "Pesquisando em fontes oficiais e gerando o texto. Isso pode levar até 1 minuto..."
                        : usesImagePipeline(sub.code)
                          ? "Gerando conteúdo e buscando imagens. Isso pode levar 1 a 2 minutos..."
                          : "Gerando conteúdo com IA, aguarde..." }}
                    </p>
                  </div>

                  <!-- Editor -->
                  <vue-editor
                    v-else
                    :value="subContents[sub.code]"
                    @input="onSubInput(sub.code, $event)"
                    :editor-toolbar="toolbar"
                    useCustomImageHandler
                    @image-added="handleImageUpload"
                  ></vue-editor>
                </div>
              </b-collapse>
            </div>
          </div>
        </div>

        <!-- Painel simples (etapas sem sub-etapas) -->
        <div
          v-else-if="selectedStage && !hasSubStages"
          class="card"
          ref="editorPanel"
        >
          <header class="card-header">
            <div class="d-flex">
              <strong class="align-self-center">
                {{ selectedStage.name }}
              </strong>
              <div class="ml-auto d-flex align-items-center">
                <small class="mr-3" :class="saveStatusClass">
                  <i :class="saveStatusIcon" class="mr-1"></i>
                  {{ saveStatusText }}
                </small>
                <button
                  class="btn btn-sm btn-success mr-2"
                  :disabled="saving || saveStatus === 'saved'"
                  @click="FlushSave"
                >
                  <i class="fa fa-save mr-1"></i>
                  Salvar
                </button>
                <button
                  class="btn btn-sm btn-outline-primary mr-2"
                  :disabled="generating"
                  @click="Generate"
                >
                  <i class="fa fa-magic mr-1"></i>
                  {{ generating ? "Gerando..." : "Gerar com IA" }}
                </button>
                <button
                  class="btn btn-sm btn-outline-dark mr-2"
                  :disabled="generating || exporting"
                  @click="ExportChapter"
                >
                  <i class="fa fa-file-word mr-1"></i>
                  {{ exporting ? "Exportando..." : "Exportar .docx" }}
                </button>
                <button
                  v-if="selectedStage.code === 'participatory_diagnosis'"
                  class="btn btn-sm btn-outline-success mr-2"
                  :disabled="exportandoExcel"
                  @click="ExportarRespostas"
                  title="Baixar respostas do formulário em Excel"
                >
                  <i class="fa fa-file-excel mr-1"></i>
                  {{ exportandoExcel ? "Exportando..." : "Baixar respostas (.xlsx)" }}
                </button>
                <button class="btn btn-sm btn-secondary" @click="CloseStage">
                  Voltar
                </button>
              </div>
            </div>
          </header>
          <div class="card-body">
            <div v-if="loadingContent" class="text-center py-4">
              <RotateSquare
                size="60px"
                class="loading-position animated fadeIn"
              ></RotateSquare>
            </div>
            <div v-else-if="generating" class="text-center py-4">
              <RotateSquare
                size="60px"
                class="loading-position animated fadeIn"
              ></RotateSquare>
              <p class="text-muted mt-3">
                Gerando conteúdo com IA, aguarde...
              </p>
            </div>
            <div v-else>
              <vue-editor
                v-model="editorContent"
                :editor-toolbar="toolbar"
                useCustomImageHandler
                @image-added="handleImageUpload"
              ></vue-editor>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import { VueEditor } from "vue2-editor";
import PmauService from "../../servico/PmauService";
import ArquivoServico from "../../servico/ArquivoServico";
import SurveyService from "../../servico/SurveyService";
import { preprocessForQuill, postprocessFromQuill } from "../../util/QuillConfig";

const IMPLEMENTED_STAGES = ["introduction", "participatory_diagnosis"];
const AUTOSAVE_DELAY = 3000;

// Sub-etapas com geração de IA disponível.
// Adicionar aqui à medida que cada sub-etapa for implementada no backend.
const IMPLEMENTED_SUB_STAGES = [
  "introduction_history",
  "introduction_importance",
  "introduction_objectives",
  "characterization_location",
  "characterization_vegetation",
  "characterization_hydrography",
  "characterization_soil",
  "characterization_climate",
  "characterization_population",
  "characterization_socioeconomic",
  "characterization_urban_area",
  "characterization_legislation",
  "urban_planning_spaces",
  "urban_planning_sidewalk_width",
  "urban_planning_property",
  "urban_planning_electrical",
  "urban_planning_road_use",
  "urban_planning_urban_furniture",
  "urban_planning_origin",
];

// Sub-etapas que utilizam web search (geração demora mais; exibe mensagem contextual).
const WEB_SEARCH_STAGES = ["characterization_location", "characterization_soil", "characterization_climate", "characterization_population", "characterization_socioeconomic", "characterization_urban_area", "characterization_legislation", "urban_planning_spaces", "urban_planning_electrical", "urban_planning_road_use", "urban_planning_urban_furniture", "urban_planning_origin"];

// Sub-etapas que utilizam pipeline de busca de imagens (3 fases; geração demora mais).
const IMAGE_PIPELINE_STAGES = [
  "introduction_history",
  "introduction_importance",
  "characterization_vegetation",
  "characterization_hydrography",
];

export default {
  name: "Pmau",
  components: {
    RotateSquare,
    VueEditor
  },
  data() {
    return {
      loading: false,
      stages: [],
      selectedStage: null,
      toolbar: [
        [{ header: [1, 2, 3, 4, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        [{ color: [] }, { background: [] }],
        ["image"],
        ["clean"]
      ],
      // Sub-etapas (accordion)
      subContents: {},
      subSaved: {},
      subStatuses: {},
      subSaving: {},
      subGenerating: {},
      subLoading: {},
      subTimers: {},
      openPanels: {},
      selectedObjectives: [],
      // Geração em batch
      batchGenerating: false,
      batchProgress: { current: 0, total: 0 },
      batchCurrentName: "",
      batchCancelled: false,
      exporting: false,
      exportandoExcel: false,
      // Etapa simples (fallback futuro)
      editorContent: "",
      savedContent: "",
      saveStatus: "saved",
      saving: false,
      generating: false,
      loadingContent: false,
      autoSaveTimer: null
    };
  },
  computed: {
    contaId() {
      var auth = this.$store.getters.getAutenticacao;
      return auth && auth.contaSelecionada ? auth.contaSelecionada.id : null;
    },
    hasSubStages() {
      return (
        this.selectedStage &&
        this.selectedStage.sub_stages &&
        this.selectedStage.sub_stages.length > 0
      );
    },
    anySubGenerating() {
      return Object.values(this.subGenerating).some(v => v);
    },
    saveStatusText() {
      if (this.saving) return "Salvando...";
      if (this.saveStatus === "saved") return "Salvo";
      if (this.saveStatus === "pending") return "Alterações pendentes";
      if (this.saveStatus === "error") return "Erro ao salvar";
      return "";
    },
    saveStatusClass() {
      if (this.saving) return "text-muted";
      if (this.saveStatus === "saved") return "text-success";
      if (this.saveStatus === "pending") return "text-warning";
      if (this.saveStatus === "error") return "text-danger";
      return "text-muted";
    },
    saveStatusIcon() {
      if (this.saving) return "fa fa-spinner fa-spin";
      if (this.saveStatus === "saved") return "fa fa-check";
      if (this.saveStatus === "pending") return "fa fa-pencil-alt";
      if (this.saveStatus === "error") return "fa fa-exclamation-triangle";
      return "";
    }
  },
  watch: {
    editorContent(val) {
      if (this.loadingContent || this.generating) return;
      if (val === this.savedContent) {
        this.saveStatus = "saved";
        return;
      }
      this.saveStatus = "pending";
      this.ScheduleAutoSave();
    }
  },
  beforeMount() {
    if (!this.contaId) {
      this.$notify({
        data: ["Conta não identificada. Faça login novamente."],
        type: "warn",
        duration: 5000
      });
      return;
    }
    this.LoadStages();
  },
  beforeDestroy() {
    this.ClearAllTimers();
  },
  beforeRouteLeave(to, from, next) {
    if (this.batchGenerating) {
      if (confirm("Geração em andamento. Deseja cancelar e sair da página?")) {
        this.batchCancelled = true;
        this.batchGenerating = false;
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  methods: {
    hasContent(html) {
      if (!html) return false;
      var text = html.replace(/<[^>]*>/g, "").trim();
      return text.length > 0;
    },

    normalizeHtml(html) {
      if (!html) return html;
      return html.replace(/>\s+</g, "><").trim();
    },

    isImplemented(stage) {
      if (stage.sub_stages && stage.sub_stages.length > 0) return true;
      return IMPLEMENTED_STAGES.includes(stage.code);
    },

    isSubImplemented(code) {
      return IMPLEMENTED_SUB_STAGES.includes(code);
    },

    usesWebSearch(code) {
      return WEB_SEARCH_STAGES.includes(code);
    },

    usesImagePipeline(code) {
      return IMAGE_PIPELINE_STAGES.includes(code);
    },

    // === Sub-etapa: status helpers ===

    getSubStatusText(code) {
      if (this.subSaving[code]) return "Salvando...";
      var s = this.subStatuses[code];
      if (s === "saved") return "Salvo";
      if (s === "pending") return "Alterações pendentes";
      if (s === "error") return "Erro ao salvar";
      return "";
    },
    getSubStatusClass(code) {
      if (this.subSaving[code]) return "text-muted";
      var s = this.subStatuses[code];
      if (s === "saved") return "text-success";
      if (s === "pending") return "text-warning";
      if (s === "error") return "text-danger";
      return "text-muted";
    },
    getSubStatusIcon(code) {
      if (this.subSaving[code]) return "fa fa-spinner fa-spin";
      var s = this.subStatuses[code];
      if (s === "saved") return "fa fa-check";
      if (s === "pending") return "fa fa-pencil-alt";
      if (s === "error") return "fa fa-exclamation-triangle";
      return "";
    },

    // === Accordion ===

    toggleAccordion(code) {
      this.$set(this.openPanels, code, !this.openPanels[code]);
    },

    // === Sub-etapa: auto-save ===

    ScheduleSubAutoSave(code) {
      if (this.subTimers[code]) clearTimeout(this.subTimers[code]);
      this.$set(
        this.subTimers,
        code,
        setTimeout(() => {
          this.AutoSaveSubStage(code);
        }, AUTOSAVE_DELAY)
      );
    },

    AutoSaveSubStage(code) {
      if (!this.selectedStage) return;
      if (this.subContents[code] === this.subSaved[code]) return;
      if (this.subSaving[code]) {
        this.ScheduleSubAutoSave(code);
        return;
      }

      this.$set(this.subSaving, code, true);

      PmauService.SaveContent(this.contaId, code, postprocessFromQuill(this.subContents[code]))
        .then(() => {
          this.$set(this.subSaving, code, false);
          this.$set(this.subSaved, code, this.subContents[code]);
          this.$set(this.subStatuses, code, "saved");
        })
        .catch((erro) => {
          this.$set(this.subSaving, code, false);
          this.$set(this.subStatuses, code, "error");
          if (erro?.response?.status === 403) {
            this.$notify({ data: [erro.response.data?.detail || "Acesso ao PMAU desabilitado."], type: "error", duration: 8000 });
            return;
          }
          this.ScheduleSubAutoSave(code);
        });
    },

    FlushSubSave(code) {
      if (this.subTimers[code]) clearTimeout(this.subTimers[code]);
      if (!this.selectedStage) return;
      if (this.subContents[code] === this.subSaved[code]) return;
      if (this.subSaving[code]) return;

      this.$set(this.subSaving, code, true);

      PmauService.SaveContent(this.contaId, code, postprocessFromQuill(this.subContents[code]))
        .then(() => {
          this.$set(this.subSaving, code, false);
          this.$set(this.subSaved, code, this.subContents[code]);
          this.$set(this.subStatuses, code, "saved");
        })
        .catch((erro) => {
          this.$set(this.subSaving, code, false);
          this.$set(this.subStatuses, code, "error");
          if (erro?.response?.status === 403) {
            this.$notify({ data: [erro.response.data?.detail || "Acesso ao PMAU desabilitado."], type: "error", duration: 8000 });
          }
        });
    },

    onSubInput(code, value) {
      this.$set(this.subContents, code, value);
      if (this.subLoading[code] || this.subGenerating[code]) return;
      if (value === this.subSaved[code]) {
        this.$set(this.subStatuses, code, "saved");
        return;
      }
      this.$set(this.subStatuses, code, "pending");
      this.ScheduleSubAutoSave(code);
    },

    // === Sub-etapa: carregar e gerar ===

    LoadSubContent(code) {
      this.$set(this.subLoading, code, true);

      PmauService.GetContent(this.contaId, code)
        .then((response) => {
          if (response.data) {
            var normalized = preprocessForQuill(this.normalizeHtml(response.data.content));
            this.$set(this.subContents, code, normalized);
            this.$set(this.subSaved, code, normalized);
          }
          this.$set(this.subStatuses, code, "saved");
          // Mantém subLoading=true até depois do Quill processar o conteúdo,
          // evitando que onSubInput dispare auto-save com o HTML sanitizado pelo Quill.
          this.$nextTick(() => {
            this.$set(this.subLoading, code, false);
            // Sincroniza subSaved com o que o Quill produziu, para que edições
            // futuras do usuário sejam corretamente detectadas como alterações.
            this.$set(this.subSaved, code, this.subContents[code]);
          });
        })
        .catch(() => {
          this.$set(this.subLoading, code, false);
        });
    },

    GenerateSubStage(sub) {
      var request = {};

      if (sub.has_selectable_options) {
        if (this.selectedObjectives.length === 0) {
          this.$notify({
            data: ["Selecione pelo menos um objetivo específico."],
            type: "warn",
            duration: 5000
          });
          return;
        }
        request.selected_objectives = this.selectedObjectives;
      }

      if (this.hasContent(this.subContents[sub.code])) {
        if (!confirm("Já existe conteúdo nesta seção. Deseja gerar novamente? O conteúdo atual será substituído.")) {
          return;
        }
      }

      this.$set(this.subGenerating, sub.code, true);
      if (this.subTimers[sub.code]) clearTimeout(this.subTimers[sub.code]);

      PmauService.GenerateStage(this.contaId, sub.code, request)
        .then((response) => {
          var rawContent = this.normalizeHtml(response.data.content);
          // Salva o HTML limpo imediatamente no banco (antes do Quill processar).
          PmauService.SaveContent(this.contaId, sub.code, rawContent)
            .catch(() => {});
          // Injeta no editor a versão pré-processada (elementos complexos em wrappers
          // ql-html-embed), para que o Quill os preserve em vez de descartá-los.
          // subGenerating permanece true aqui: onSubInput não agendará auto-save
          // enquanto o Quill estiver processando o HTML recém-recebido.
          var quillContent = preprocessForQuill(rawContent);
          this.$set(this.subContents, sub.code, quillContent);
          this.$set(this.subSaved, sub.code, quillContent);
          this.$set(this.subStatuses, sub.code, "saved");
          this.$set(this.openPanels, sub.code, true);
          // Só libera subGenerating após o Quill ter processado o conteúdo,
          // e sincroniza subSaved com o output real do Quill para detectar edições futuras.
          this.$nextTick(() => {
            this.$set(this.subGenerating, sub.code, false);
            this.$set(this.subSaved, sub.code, this.subContents[sub.code]);
          });
          this.$notify({
            data: ["Conteúdo gerado com sucesso!"],
            type: "success",
            duration: 5000
          });
        })
        .catch((erro) => {
          this.$set(this.subGenerating, sub.code, false);
          this.$notify({
            data: [
              erro.response && erro.response.data
                ? erro.response.data.detail || "Erro ao gerar conteúdo"
                : "Erro ao conectar com o serviço de IA"
            ],
            type: "warn",
            duration: 5000
          });
        });
    },

    async GenerateAllSubStages() {
      if (!this.selectedStage || !this.hasSubStages) return;

      var implementedSubs = this.selectedStage.sub_stages.filter(sub =>
        this.isSubImplemented(sub.code)
      );

      if (implementedSubs.length === 0) {
        this.$notify({
          data: ["Nenhuma sub-etapa disponível para geração neste capítulo."],
          type: "warn",
          duration: 5000
        });
        return;
      }

      var hasExisting = implementedSubs.some(sub => this.hasContent(this.subContents[sub.code]));
      if (hasExisting) {
        if (!confirm("Já existe conteúdo em uma ou mais sub-etapas. Deseja gerar todo o capítulo? Os conteúdos existentes serão substituídos.")) {
          return;
        }
      }

      this.batchGenerating = true;
      this.batchCancelled = false;
      this.batchProgress = { current: 0, total: implementedSubs.length };
      this.batchCurrentName = "";

      var failed = [];
      var skipped = [];

      for (var i = 0; i < implementedSubs.length; i++) {
        if (this.batchCancelled) break;

        var sub = implementedSubs[i];
        this.batchProgress = { current: i + 1, total: implementedSubs.length };
        this.batchCurrentName = sub.name;

        var request = {};
        if (sub.has_selectable_options) {
          if (this.selectedObjectives.length === 0) {
            skipped.push(sub.name);
            continue;
          }
          request.selected_objectives = this.selectedObjectives;
        }

        this.$set(this.subGenerating, sub.code, true);
        if (this.subTimers[sub.code]) clearTimeout(this.subTimers[sub.code]);

        try {
          var response = await PmauService.GenerateStage(this.contaId, sub.code, request);
          var rawContent = this.normalizeHtml(response.data.content);
          PmauService.SaveContent(this.contaId, sub.code, rawContent).catch(() => {});
          var quillContent = preprocessForQuill(rawContent);
          this.$set(this.subContents, sub.code, quillContent);
          this.$set(this.subSaved, sub.code, quillContent);
          this.$set(this.subStatuses, sub.code, "saved");
          this.$set(this.openPanels, sub.code, true);
          await this.$nextTick();
          this.$set(this.subGenerating, sub.code, false);
          this.$set(this.subSaved, sub.code, this.subContents[sub.code]);
        } catch (erro) {
          this.$set(this.subGenerating, sub.code, false);
          if (!this.batchCancelled) failed.push(sub.name);
        }
      }

      this.batchGenerating = false;
      this.batchProgress = { current: 0, total: 0 };
      this.batchCurrentName = "";

      if (this.batchCancelled) {
        this.$notify({
          data: ["Geração cancelada."],
          type: "warn",
          duration: 5000
        });
        return;
      }

      if (skipped.length > 0) {
        this.$notify({
          data: ["Sub-etapas puladas (nenhum objetivo selecionado): " + skipped.join(", ")],
          type: "warn",
          duration: 8000
        });
      }
      if (failed.length > 0) {
        this.$notify({
          data: ["Erro ao gerar: " + failed.join(", ")],
          type: "warn",
          duration: 8000
        });
      }
      if (failed.length === 0 && skipped.length === 0) {
        this.$notify({
          data: ["Capítulo gerado com sucesso!"],
          type: "success",
          duration: 5000
        });
      } else if (failed.length < implementedSubs.length) {
        this.$notify({
          data: [`${implementedSubs.length - failed.length - skipped.length} de ${implementedSubs.length} sub-etapas geradas com sucesso.`],
          type: "success",
          duration: 5000
        });
      }
    },

    CancelBatch() {
      if (!confirm("Deseja cancelar a geração em andamento?")) return;
      this.batchCancelled = true;
      this.batchGenerating = false;
      this.batchCurrentName = "";
      this.batchProgress = { current: 0, total: 0 };
    },

    // === Gerenciamento de etapas ===

    ClearAllTimers() {
      // Sub-etapa timers
      Object.keys(this.subTimers).forEach((code) => {
        if (this.subTimers[code]) clearTimeout(this.subTimers[code]);
      });
      // Etapa simples timer
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer);
        this.autoSaveTimer = null;
      }
    },

    LoadStages() {
      this.loading = true;
      this.selectedStage = null;

      PmauService.GetStages()
        .then((response) => {
          this.loading = false;
          this.stages = response.data;
        })
        .catch((erro) => {
          this.loading = false;
          this.$notify({
            data: [
              erro.response && erro.response.data
                ? erro.response.data.detail || "Erro ao carregar etapas"
                : "Erro ao conectar com o serviço de IA"
            ],
            type: "warn",
            duration: 5000
          });
        });
    },

    OpenStage(stage) {
      this.selectedStage = stage;

      if (stage.sub_stages && stage.sub_stages.length > 0) {
        // Inicializa estado de cada sub-etapa
        stage.sub_stages.forEach((sub) => {
          this.$set(this.subContents, sub.code, "");
          this.$set(this.subSaved, sub.code, "");
          this.$set(this.subStatuses, sub.code, "saved");
          this.$set(this.subSaving, sub.code, false);
          this.$set(this.subGenerating, sub.code, false);
          this.$set(this.subLoading, sub.code, false);
          this.$set(this.openPanels, sub.code, false);
        });

        // Carrega objetivos se houver
        this.selectedObjectives = [];

        // Carrega conteúdo de cada sub-etapa
        stage.sub_stages.forEach((sub) => {
          this.LoadSubContent(sub.code);
        });
      } else {
        // Etapa simples
        this.editorContent = "";
        this.savedContent = "";
        this.saveStatus = "saved";
        this.LoadContent();
      }

      this.$nextTick(() => {
        if (this.$refs.editorPanel) {
          this.$refs.editorPanel.scrollIntoView({ behavior: "smooth" });
        }
      });
    },

    CloseStage() {
      if (this.batchGenerating) {
        if (!confirm("Geração em andamento. Deseja cancelar e voltar?")) return;
        this.batchCancelled = true;
        this.batchGenerating = false;
        this.batchCurrentName = "";
        this.batchProgress = { current: 0, total: 0 };
      }
      if (this.hasSubStages) {
        this.selectedStage.sub_stages.forEach((sub) => {
          this.FlushSubSave(sub.code);
          if (this.subTimers[sub.code]) clearTimeout(this.subTimers[sub.code]);
        });
        this.subContents = {};
        this.subSaved = {};
        this.subStatuses = {};
        this.subSaving = {};
        this.subGenerating = {};
        this.subLoading = {};
        this.subTimers = {};
        this.openPanels = {};
        this.selectedObjectives = [];
      } else {
        this.FlushSave();
        this.ClearAutoSave();
      }
      this.selectedStage = null;
      this.editorContent = "";
      this.savedContent = "";
      this.saveStatus = "saved";
    },

    // === Etapa simples (fallback) ===

    ClearAutoSave() {
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer);
        this.autoSaveTimer = null;
      }
    },

    ScheduleAutoSave() {
      this.ClearAutoSave();
      this.autoSaveTimer = setTimeout(() => {
        this.AutoSave();
      }, AUTOSAVE_DELAY);
    },

    AutoSave() {
      if (!this.selectedStage) return;
      if (this.editorContent === this.savedContent) return;
      if (this.saving) {
        this.ScheduleAutoSave();
        return;
      }
      this.saving = true;
      PmauService.SaveContent(
        this.contaId,
        this.selectedStage.code,
        postprocessFromQuill(this.editorContent)
      )
        .then(() => {
          this.saving = false;
          this.savedContent = this.editorContent;
          this.saveStatus = "saved";
        })
        .catch((erro) => {
          this.saving = false;
          this.saveStatus = "error";
          if (erro?.response?.status === 403) {
            this.$notify({ data: [erro.response.data?.detail || "Acesso ao PMAU desabilitado."], type: "error", duration: 8000 });
            return;
          }
          this.ScheduleAutoSave();
        });
    },

    FlushSave() {
      this.ClearAutoSave();
      if (!this.selectedStage) return;
      if (this.editorContent === this.savedContent) return;
      if (this.saving) return;
      this.saving = true;
      return PmauService.SaveContent(
        this.contaId,
        this.selectedStage.code,
        postprocessFromQuill(this.editorContent)
      )
        .then(() => {
          this.saving = false;
          this.savedContent = this.editorContent;
          this.saveStatus = "saved";
        })
        .catch((erro) => {
          this.saving = false;
          this.saveStatus = "error";
          if (erro?.response?.status === 403) {
            this.$notify({ data: [erro.response.data?.detail || "Acesso ao PMAU desabilitado."], type: "error", duration: 8000 });
          }
        });
    },

    LoadContent() {
      this.loadingContent = true;
      PmauService.GetContent(this.contaId, this.selectedStage.code)
        .then((response) => {
          if (response.data) {
            var normalized = preprocessForQuill(this.normalizeHtml(response.data.content));
            this.editorContent = normalized;
            this.savedContent = normalized;
          }
          this.saveStatus = "saved";
          this.$nextTick(() => {
            this.loadingContent = false;
            this.savedContent = this.editorContent;
          });
        })
        .catch(() => {
          this.loadingContent = false;
        });
    },

    Generate() {
      if (this.hasContent(this.editorContent)) {
        if (!confirm("Já existe conteúdo nesta seção. Deseja gerar novamente? O conteúdo atual será substituído.")) {
          return;
        }
      }

      this.generating = true;
      this.ClearAutoSave();
      PmauService.GenerateStage(this.contaId, this.selectedStage.code, {})
        .then((response) => {
          var rawContent = this.normalizeHtml(response.data.content);
          PmauService.SaveContent(this.contaId, this.selectedStage.code, rawContent).catch(() => {});
          var quillContent = preprocessForQuill(rawContent);
          this.editorContent = quillContent;
          this.savedContent = quillContent;
          this.saveStatus = "saved";
          this.$nextTick(() => {
            this.generating = false;
            this.savedContent = this.editorContent;
          });
          this.$notify({
            data: ["Conteúdo gerado com sucesso!"],
            type: "success",
            duration: 5000
          });
        })
        .catch((erro) => {
          this.generating = false;
          this.$notify({
            data: [
              erro.response && erro.response.data
                ? erro.response.data.detail || "Erro ao gerar conteúdo"
                : "Erro ao conectar com o serviço de IA"
            ],
            type: "warn",
            duration: 5000
          });
        });
    },

    // === Exportar capítulo como .docx ===

    async ExportChapter() {
      if (this.exporting || !this.selectedStage) return;
      this.exporting = true;
      try {
        var res = await PmauService.ExportChapter(this.contaId, this.selectedStage.code);
        var blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        });
        var url = URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = url;
        a.download = this.selectedStage.code.replace(/_/g, "-") + ".docx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        this.$notify({
          data: ["Erro ao exportar capítulo."],
          type: "warn",
          duration: 5000
        });
      } finally {
        this.exporting = false;
      }
    },

    async ExportarRespostas() {
      if (this.exportandoExcel || !this.contaId) return;
      this.exportandoExcel = true;
      try {
        var formRes = await SurveyService.GetForm(this.contaId);
        var formId = formRes.data.id;
        var res = await SurveyService.ExportResponses(formId);
        var blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        var url = URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = url;
        a.download = "diagnostico-participativo-respostas.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        this.$notify({
          data: ["Erro ao exportar respostas."],
          type: "warn",
          duration: 5000
        });
      } finally {
        this.exportandoExcel = false;
      }
    },

    // === Upload de imagens ===

    handleImageUpload(file, Editor, cursorLocation, resetUploader) {
      ArquivoServico.New([file])
        .then((response) => {
          var fileId = response.data[0];
          var baseUrl = this.$store.getters.baseURL;
          var url = baseUrl + "arquivo/get/" + fileId;
          Editor.insertEmbed(cursorLocation, "image", url);
          resetUploader();
        })
        .catch(() => {
          this.$notify({
            data: ["Erro ao enviar imagem. Tente novamente."],
            type: "warn",
            duration: 5000
          });
          resetUploader();
        });
    }
  }
};
</script>

<style scoped>
.accordion-header {
  cursor: pointer;
}
.accordion-header:hover {
  background-color: #f8f9fa;
}
.form-check-label {
  font-size: 0.9rem;
}
.batch-progress-banner {
  background-color: #e8f4fd;
  position: sticky;
  top: 0;
  z-index: 10;
}
</style>

<style>
/* Imagens dentro do editor Quill (unscoped pois Quill gera HTML próprio) */
.ql-editor img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 1rem auto;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Blocos HTML preservados (tabelas, divs estilizados, imagens com legenda).
   Renderizados como blocos não-editáveis dentro do editor. */
.ql-editor .ql-html-embed {
  cursor: default;
  margin: 8px 0;
  overflow-x: auto;
}
.ql-editor .ql-html-embed table {
  width: 100%;
  border-collapse: collapse;
}
.ql-editor .ql-html-embed th,
.ql-editor .ql-html-embed td {
  border: 1px solid #999;
  padding: 8px;
}
</style>
